import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import validator from "validator";
import {
  actionCityList,
  actionCountryList,
  actionStateList,
} from "../../services/CommonService";
import {
  getSubscriptionList,
  signUp,
} from "../../services/AuthServices";
import Utils from "../../utils";
import { toast } from "react-toastify";
import Select from "react-select";
import useSeo from "../../utils/useSeo";
import "../../asset/style/auth.css";

const Field = ({ label, children, error }) => (
  <div className="tp-auth-field">
    <label>{label}</label>
    {children}
    {error && <span className="tp-auth-error">{error}</span>}
  </div>
);

const selectDark = {
  control: (base, state) => ({
    ...base,
    background: "#171B20",
    borderColor: state.isFocused ? "#8B5CF6" : "#242A31",
    color: "#E7EAEE",
    borderRadius: 10,
    minHeight: 44,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(139,92,246,0.18)" : "none",
    "&:hover": { borderColor: "#8B5CF6" },
  }),
  menu: (base) => ({
    ...base,
    background: "#171B20",
    border: "1px solid #242A31",
    borderRadius: 10,
    boxShadow: "0 20px 45px -20px rgba(0,0,0,0.55)",
    overflow: "hidden",
    zIndex: 50,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({
    ...base,
    background: "#171B20",
    padding: 6,
    maxHeight: 260,
    "::-webkit-scrollbar": { width: 8 },
    "::-webkit-scrollbar-thumb": { background: "#2A3039", borderRadius: 4 },
  }),
  option: (base, { isFocused, isSelected, isDisabled }) => ({
    ...base,
    background: isSelected
      ? "rgba(139,92,246,0.28)"
      : isFocused
      ? "rgba(139,92,246,0.14)"
      : "#171B20",
    color: isDisabled ? "#5B6370" : "#E7EAEE",
    fontWeight: isSelected ? 600 : 400,
    borderRadius: 8,
    padding: "10px 12px",
    cursor: isDisabled ? "not-allowed" : "pointer",
    "&:active": { background: "rgba(139,92,246,0.32)" },
  }),
  singleValue: (base) => ({ ...base, color: "#E7EAEE" }),
  multiValue: (base) => ({ ...base, background: "rgba(139,92,246,0.2)", borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: "#E7EAEE" }),
  placeholder: (base) => ({ ...base, color: "#9AA3AD" }),
  input: (base) => ({ ...base, color: "#E7EAEE" }),
  noOptionsMessage: (base) => ({ ...base, color: "#9AA3AD", background: "#171B20" }),
  loadingMessage: (base) => ({ ...base, color: "#9AA3AD", background: "#171B20" }),
  indicatorSeparator: (base) => ({ ...base, background: "#242A31" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#A78BFA" : "#6A7280",
    "&:hover": { color: "#A78BFA" },
  }),
  clearIndicator: (base) => ({ ...base, color: "#6A7280", "&:hover": { color: "#EF4444" } }),
};

const Signup = () => {
  const navigate = useNavigate();
  const [zipCode, setZipCode] = useState("");
  const [signupData, setSignUpData] = useState({ country: "", state: "", city: "" });

  useSeo({
    title: "Registo · Crie a sua conta",
    description:
      "Crie a sua conta na TP Software e comece a gerir o seu centro de abate automóvel em minutos. Veículos, inventário, despoluição LER e vendas numa só plataforma.",
    keywords: "registo TP Software, criar conta, signup, centro de abate automóvel",
    path: "/signup",
  });

  let errObj = {
    name: "", email: "", password: "", address: "", address_proof: "",
    company_registration_number: "", state: "", country: "", zipCode: "", city: "",
  };
  const [errors, setErrors] = useState(errObj);
  const [address, setAddress] = useState("");
  const [address_proof, setAddressProof] = useState("");
  const [company_registration_number, setCompanyRegistrationNumber] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    actionCountryList()
      .then((r) => {
        setCountryList((r.data.data || []).map((c) => ({ value: c.id, label: c.name })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (signupData.country) {
      actionStateList({ country_id: signupData.country })
        .then((r) => setStateList((r.data.data || []).map((s) => ({ value: s.id, label: s.name }))))
        .catch(() => {});
    }
  }, [signupData.country]);

  useEffect(() => {
    if (!signupData.state) {
      setCityList([]);
      return;
    }
    actionCityList({ state_id: signupData.state })
      .then((r) => setCityList((r.data.data || []).map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => setCityList([]));
  }, [signupData.state]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setAddressProof(f);
    setSelectedFileName(f ? f.name : "");
  };

  const validateImage = (file) => {
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) return false;
    return file.size <= 5 * 1024 * 1024;
  };

  const onSignUp = (e) => {
    e.preventDefault();
    let eObj = { ...errObj };
    let error = false;

    if (!name) { eObj.name = "Nome é obrigatório"; error = true; }
    if (!email) { eObj.email = "Email é obrigatório"; error = true; }
    else if (!validator.isEmail(email)) { eObj.email = "Email inválido"; error = true; }
    if (!password) { eObj.password = "Palavra-passe é obrigatória"; error = true; }
    if (!address) { eObj.address = "Morada é obrigatória"; error = true; }
    if (!address_proof) { eObj.address_proof = "Comprovativo é obrigatório"; error = true; }
    else if (!validateImage(address_proof)) { eObj.address_proof = "Ficheiro inválido (max 5MB, JPG/PNG/GIF)"; error = true; }
    if (!signupData.country) { eObj.country = "País é obrigatório"; error = true; }
    if (!signupData.state) { eObj.state = "Distrito é obrigatório"; error = true; }
    if (cityList.length > 0 && !signupData.city) { eObj.city = "Concelho é obrigatório"; error = true; }
    if (!zipCode) { eObj.zipCode = "Código postal é obrigatório"; error = true; }
    if (!company_registration_number) { eObj.company_registration_number = "NIF é obrigatório"; error = true; }

    setErrors(eObj);
    if (error) return;

    const fd = new FormData();
    fd.append("company_name", name);
    fd.append("email", email);
    fd.append("address", address);
    fd.append("password", password);
    fd.append("company_registration_number", company_registration_number);
    fd.append("address_proof", address_proof);
    fd.append("country", signupData.country);
    fd.append("state", signupData.state);
    fd.append("zipcode", zipCode);
    fd.append("city", signupData.city);

    setIsLoading(true);
    signUp(fd)
      .then((response) => {
        toast.success("Conta criada com sucesso! Faça login para continuar.");
        localStorage.removeItem("hasSeenMigrateWizard");
        setTimeout(() => navigate("/login", { replace: true }), 1500);
      })
      .catch((error) => toast.error(Utils.getErrorMessage(error)))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="tp-auth">
      <div className="tp-auth-left">
        <div className="tp-auth-left-content">
          <img
            src="https://tp.software/libs/images/tpsoftware-green-white.png"
            alt="TP Software"
            className="tp-auth-logo"
          />
          <h1>Registe a sua empresa.</h1>
          <p>Comece a gerir o seu centro de desmantelamento em minutos. 14 dias grátis.</p>
          <div className="tp-auth-stats">
            <div><strong>+30</strong><span>Centros</span></div>
            <div><strong>+1M</strong><span>Peças</span></div>
            <div><strong>25K</strong><span>Viaturas</span></div>
          </div>
        </div>
      </div>

      <div className="tp-auth-right" style={{ overflowY: "auto" }}>
        <div className="tp-auth-card" style={{ maxWidth: 520 }}>
          <h2>Criar conta</h2>
          <p className="tp-auth-sub">Preencha os dados da sua empresa para começar.</p>

          <form onSubmit={onSignUp}>
            <div className="tp-auth-field-row">
              <Field label="Nome da empresa" error={errors.name}>
                <input className="tp-auth-input" placeholder="Empresa, Lda" value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Email" error={errors.email}>
                <input className="tp-auth-input" type="email" placeholder="geral@empresa.com" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
            </div>

            <div className="tp-auth-field-row">
              <Field label="Palavra-passe" error={errors.password}>
                <input className="tp-auth-input" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
              <Field label="NIF" error={errors.company_registration_number}>
                <input className="tp-auth-input" placeholder="123456789" value={company_registration_number} onChange={(e) => setCompanyRegistrationNumber(e.target.value)} />
              </Field>
            </div>

            <div className="tp-auth-field-row">
              <Field label="País" error={errors.country}>
                <Select
                  styles={selectDark}
                  classNamePrefix="tp-select"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  options={countryList}
                  placeholder="Selecionar..."
                  value={countryList.find((o) => o.value === Number(signupData.country))}
                  noOptionsMessage={() => "Sem opções"}
                  onChange={(o) => {
                    setSignUpData({ country: o.value, state: "", city: "" });
                    setStateList([]);
                    setCityList([]);
                  }}
                />
              </Field>
              <Field label="Distrito" error={errors.state}>
                <Select
                  styles={selectDark}
                  classNamePrefix="tp-select"
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  options={stateList}
                  placeholder="Selecionar..."
                  noOptionsMessage={() => "Sem opções"}
                  value={stateList.find((o) => o.value === Number(signupData.state))}
                  onChange={(o) => setSignUpData({ ...signupData, state: o.value, city: "" })}
                />
              </Field>
            </div>

            <div className="tp-auth-field-row">
              {cityList.length > 0 ? (
                <Field label="Concelho" error={errors.city}>
                  <Select
                    styles={selectDark}
                    classNamePrefix="tp-select"
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    options={cityList}
                    placeholder="Selecionar..."
                    noOptionsMessage={() => "Sem opções"}
                    value={cityList.find((o) => o.value === Number(signupData.city))}
                    onChange={(o) => setSignUpData({ ...signupData, city: o.value })}
                  />
                </Field>
              ) : null}
              <Field label="Código postal" error={errors.zipCode}>
                <input className="tp-auth-input" placeholder="1000-001" value={zipCode} onChange={(e) => setZipCode(e.target.value)} />
              </Field>
            </div>

            <Field label="Comprovativo de atividade" error={errors.address_proof}>
              <label className="tp-auth-file-label">
                <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: "none" }} />
                📎 {selectedFileName || "Carregar ficheiro (JPG/PNG, max 5MB)"}
              </label>
            </Field>

            <Field label="Morada" error={errors.address}>
              <textarea className="tp-auth-input" rows={2} placeholder="Rua, nº, andar" value={address} onChange={(e) => setAddress(e.target.value)} style={{ resize: "vertical" }} />
            </Field>

            <button type="submit" className="tp-auth-submit" disabled={isLoading} style={{ marginTop: 8 }}>
              {isLoading ? "A criar conta..." : "Criar conta"}
            </button>
          </form>

          <div className="tp-auth-footer">
            Já tem conta? <Link to="/login">Entrar</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
